import React, { useMemo, useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ContentPageItem,
  useSearchContentPages,
} from "../../api-data/service-provider/manage-contents";

import MorePage from "./MorePage";
import DesktopFooter from "./DesktopFooter";
import MobileNavBar from "./MobileNavBar";

const getLatestPage = (pages: ContentPageItem[]) =>
  [...pages].sort((a, b) => {
    const aTime = a.updateTimestamp ?? a.createTimestamp;
    const bTime = b.updateTimestamp ?? b.createTimestamp;
    return bTime - aTime;
  })[0];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Redirect /more to home on desktop since it's mobile-only
  useEffect(() => {
    if (isMdUp && location.pathname === "/more") {
      navigate("/", { replace: true });
    }
  }, [isMdUp, location.pathname, navigate]);

  const { data } = useSearchContentPages(50, 0);

  const pages = useMemo(() => data?.pages?.[0]?.data ?? [], [data]);

  const footerPages = useMemo(() => {
    const grouped: Record<string, ContentPageItem[]> = {};
    pages.forEach((p: ContentPageItem) => {
      grouped[p.category] = grouped[p.category] || [];
      grouped[p.category].push(p);
    });
    return Object.values(grouped).map(getLatestPage);
  }, [pages]);

  const active = useMemo(() => {
    if (location.pathname.replace(/\/$/, "") === "/content/help") return "Help";
    if (location.pathname === "/") return "Home";
    if (location.pathname.startsWith("/account/dashboard")) return "Dashboard";
    if (
      location.pathname.startsWith("/admin") ||
      location.pathname.startsWith("/field-operator")
    )
      return "Admin";
    if (location.pathname.startsWith("/favorites")) return "Favorites";
    if (location.pathname.startsWith("/account")) return "Account";
    return "Search";
  }, [location.pathname]);

  const handleSearchClick = () => {
    const searchBar = document.querySelector<HTMLInputElement>(
      "input[data-searchbar]",
    );
    if (searchBar) {
      searchBar.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => searchBar.focus(), 350);
    } else {
      navigate("/?focus=true");
    }
  };

  const isMorePage = !isMdUp && location.pathname === "/more";

  return (
    <>
      {isMorePage && <MorePage footerPages={footerPages} />}

      {isMdUp && <DesktopFooter footerPages={footerPages} />}

      {!isMdUp && (
        <MobileNavBar active={active} onSearchClick={handleSearchClick} />
      )}
    </>
  );
};

export default Footer;
